import React from 'react';
// Import bank logos
import barclaysLogo from '../../../assets/BCS.svg';
import hsbcLogo from '../../../assets/HSBC.svg';
import lloydsLogo from '../../../assets/LYG.svg';
import monzoLogo from '../../../assets/monzo.png';
import natwestLogo from '../../../assets/NWG.png';
import starlingLogo from '../../../assets/starlingbank.svg';

const OpenBankingAnimation: React.FC = () => {
  return (
    <div className="relative">
      {/* Phone Mockup with Animation */}
      <div className="relative mx-auto w-full max-w-[280px] h-[500px] bg-gray-900 rounded-[32px] p-3 shadow-xl overflow-hidden">
        {/* Phone Frame */}
        <div className="absolute top-0 left-0 right-0 h-6 flex justify-center">
          <div className="w-24 h-5 bg-gray-900 rounded-b-xl"></div>
        </div>

        {/* Screen Content */}
        <div className="w-full h-full bg-white rounded-[24px] overflow-hidden relative">
          {/* Step 0: Checkout Page (Initially Visible) */}
          <div className="absolute inset-0 bg-white p-4 transition-all duration-500 ease-in-out animate-[fadeInOut_20s_ease-in-out_infinite]"
               style={{animationDelay: "0s"}}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <span className="text-gray-900 text-lg font-semibold">Checkout</span>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4 text-left">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Order Summary</h3>

              <div className="mb-3">
                <p className="text-sm text-gray-600">Premium Plan - Annual</p>
                <p className="text-sm font-medium">£5,000.00</p>
              </div>

              <div className="border-t border-gray-200 pt-3">
                <div className="text-sm font-medium">
                  <p className="text-gray-900">Total: £5,000.00</p>
                </div>
              </div>
            </div>

            {/* Pay Button */}
            <div className="absolute bottom-4 left-4 right-4">
              <button className="w-full bg-gray-900 text-white font-medium py-3 rounded-lg">
                Pay £5,000.00
              </button>
              <div className="flex items-center justify-center mt-3">
                <svg className="w-4 h-4 text-gray-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-xs text-gray-500">Secure payment</span>
              </div>
            </div>
          </div>

          {/* Step 1: Bank Selection (Initially Hidden) */}
          <div className="absolute inset-0 bg-white p-4 transition-all duration-500 ease-in-out opacity-0 animate-[fadeInOut_20s_ease-in-out_infinite]"
               style={{animationDelay: "5s"}}>
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Select your bank</h3>
            </div>

            {/* Search Bar */}
            <div className="bg-gray-100 rounded-full px-4 py-2 flex items-center mb-4">
              <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="text-sm text-gray-400">Search your bank</span>
            </div>

            {/* Bank List */}
            <div className="space-y-3">
              {/* Barclays */}
              <div className="flex items-center p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 flex items-center justify-center mr-3">
                  <img src={barclaysLogo} alt="Barclays" className="h-6 w-auto" />
                </div>
                <span className="text-gray-800">Barclays</span>
              </div>

              {/* HSBC */}
              <div className="flex items-center p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 flex items-center justify-center mr-3">
                  <img src={hsbcLogo} alt="HSBC" className="h-6 w-auto" />
                </div>
                <span className="text-gray-800">HSBC</span>
              </div>

              {/* Lloyds */}
              <div className="flex items-center p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 flex items-center justify-center mr-3">
                  <img src={lloydsLogo} alt="Lloyds" className="h-6 w-auto" />
                </div>
                <span className="text-gray-800">Lloyds</span>
              </div>

              {/* Monzo */}
              <div className="flex items-center p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 flex items-center justify-center mr-3">
                  <img src={monzoLogo} alt="Monzo" className="h-6 w-auto" />
                </div>
                <span className="text-gray-800">Monzo</span>
              </div>

              {/* NatWest */}
              <div className="flex items-center p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 flex items-center justify-center mr-3">
                  <img src={natwestLogo} alt="NatWest" className="h-6 w-auto" />
                </div>
                <span className="text-gray-800">NatWest</span>
              </div>

              {/* Starling */}
              <div className="flex items-center p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <div className="w-8 h-8 flex items-center justify-center mr-3">
                  <img src={starlingLogo} alt="Starling Bank" className="h-6 w-auto" />
                </div>
                <span className="text-gray-800">Starling Bank</span>
              </div>
            </div>
          </div>

          {/* Step 2: Authentication (Initially Hidden) */}
          <div className="absolute inset-0 bg-gray-200 flex flex-col items-center justify-center transition-all duration-500 ease-in-out opacity-0 animate-[fadeInOut_20s_ease-in-out_infinite]"
               style={{animationDelay: "10s"}}>
            <div className="bg-white rounded-lg p-6 shadow-md w-4/5 relative">
              <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center animate-pulse">
                  <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>

              <h3 className="text-center text-lg font-semibold mb-2">Authenticate ID</h3>
              <p className="text-center text-sm text-gray-500 mb-4">Securely connecting to your bank</p>

              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 rounded-full animate-[progress_2.5s_ease-in-out_infinite]" style={{width: "0%"}}></div>
              </div>
            </div>
          </div>

          {/* Step 3: Success (Initially Hidden) */}
          <div className="absolute inset-0 bg-white flex flex-col items-center justify-start pt-10 transition-all duration-500 ease-in-out opacity-0 animate-[fadeInOut_20s_ease-in-out_infinite]"
               style={{animationDelay: "15s"}}>
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mb-4">
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-2">Success</h3>

            <div className="w-4/5 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Amount</span>
                <span className="text-sm font-medium">£ 5,000</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">ID</span>
                <span className="text-sm font-medium">#TL0023</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Beneficiary</span>
                <span className="text-sm font-medium">QuikTrad</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Animation Keyframes */}
      <style>{`
        @keyframes fadeInOut {
          0%, 22% { opacity: 1; transform: translateX(0); }
          25%, 97% { opacity: 0; transform: translateX(-20px); }
          100% { opacity: 1; transform: translateX(0); }
        }

        @keyframes progress {
          0% { width: 0%; }
          80% { width: 90%; }
          100% { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default OpenBankingAnimation;
